import React, { useState, useEffect } from "react";
import { 
  doc, getDoc, updateDoc, 
  collection, query, where, getDocs 
} from "firebase/firestore";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { db } from "../firebase/firebase";
import { 
  Box, Container, Stack, Card, CardContent, Typography, 
  Button, CircularProgress, Alert, Switch, Chip,
  AppBar, Toolbar
} from "@mui/material";
import { styled } from "@mui/material/styles";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import { useAuth } from "../contexts/AuthContext";
import { preferencesService } from "../services/preferencesService";
import { assetService } from "../services/assetService";

// ─────────────────────────────────────────────────────────────────────────────
// STYLED COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────
const SectionCard = styled(Card)(() => ({
  borderRadius: 8,
  border: '1px solid #e2e8f0',
  boxShadow: 'none',
}));

const SectionTitle = styled(Typography)(() => ({
  fontSize: '0.9375rem', 
  fontWeight: 700, 
  color: '#1e293b',
  marginBottom: '1.25rem', 
  paddingBottom: '0.75rem',
  borderBottom: '1px solid #e5e7eb',
}));

const FieldLabel = styled(Typography)(() => ({
  fontSize: '0.75rem', 
  fontWeight: 600, 
  color: '#6b7280',
  textTransform: 'uppercase', 
  letterSpacing: '0.025em',
  marginBottom: '0.25rem',
}));

const DetailGroup = styled(Box)(() => ({
  flex: 1, 
  minWidth: '200px', 
  display: 'flex', 
  flexDirection: 'column', 
  gap: '0.25rem',
}));

const FormRow = styled(Box)(() => ({
  display: 'flex', 
  gap: '1.5rem', 
  marginBottom: '1.5rem', 
  flexWrap: 'wrap',
  '&:last-child': { marginBottom: 0 },
}));

const DetailValue = ({ label, value, isMonospace }) => (
  <DetailGroup>
    <FieldLabel>{label}</FieldLabel>
    <Typography sx={{ 
      fontSize: '0.875rem', 
      fontWeight: 500, 
      color: value && String(value).trim() !== "" ? '#1e293b' : '#9ca3af',
      fontStyle: value && String(value).trim() !== "" ? 'normal' : 'italic',
      fontFamily: isMonospace ? 'monospace' : 'inherit'
    }}>
      {value && String(value).trim() !== "" ? value : "Not Logged"}
    </Typography>
  </DetailGroup>
);

// ─────────────────────────────────────────────────────────────────────────────
// UTILS
// ─────────────────────────────────────────────────────────────────────────────
const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const PublicAssetView = () => {
  const { assetId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();

  const { currentUser } = useAuth();

  const [asset, setAsset] = useState(null);
  const [subCategory, setSubCategory] = useState(null);
  const [assignedUserData, setAssignedUserData] = useState(null);
  const [expiration, setExpiration] = useState(null);

  const [publicQrEnabled, setPublicQrEnabled] = useState(false);
  const [prefResolved, setPrefResolved] = useState(false);
  const [viewMode, setViewMode] = useState("loading");

  // Tagging state
  const [localIsTagged, setLocalIsTagged] = useState(false);
  const [tagPhoto, setTagPhoto] = useState(null);
  const [tagSubmitting, setTagSubmitting] = useState(false);
  const [tagError, setTagError] = useState("");
  const [tagSuccess, setTagSuccess] = useState(false);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load data & resolve relational references
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        if (!assetId || !token) throw new Error("Missing Credentials");

        const tokenDocId = `${token}_${assetId}`;
        const tokenRef = doc(db, "publicAssetTokens", tokenDocId);
        const tokenSnap = await getDoc(tokenRef);

        if (!tokenSnap.exists()) throw new Error("Invalid Token");
        const tokenData = tokenSnap.data();

        if (!tokenData.isActive) throw new Error("Token Inactive");
        if (tokenData.expiresAt.toDate() < new Date())
          throw new Error("Token Expired");

        setExpiration({
          expiresAt: tokenData.expiresAt.toDate().toLocaleDateString(undefined, {
            dateStyle: "medium",
          }),
        });

        const assetRef = doc(db, "assets", assetId);
        const assetSnap = await getDoc(assetRef);
        if (!assetSnap.exists()) throw new Error("Asset Not Found");

        const a = assetSnap.data();

        // Robust relational lookup helper (checks doc ID + field queries)
        const fetchRelationalDoc = async (collectionName, id, idFieldName = "id") => {
          if (!id) return null;
          try {
            // 1. Try direct doc ID match
            const docRef = doc(db, collectionName, String(id));
            const snap = await getDoc(docRef);
            if (snap.exists()) return snap.data();

            // 2. Query by specific field ID
            const colRef = collection(db, collectionName);
            let q = query(colRef, where(idFieldName, "==", id));
            let qSnap = await getDocs(q);
            if (!qSnap.empty) return qSnap.docs[0].data();

            // 3. Fallback to standard "id" field query
            if (idFieldName !== "id") {
              q = query(colRef, where("id", "==", id));
              qSnap = await getDocs(q);
              if (!qSnap.empty) return qSnap.docs[0].data();
            }

            return null;
          } catch (e) {
            console.warn(`[PublicAssetView] Lookup failed for ${collectionName} (${id}):`, e);
            return null;
          }
        };

        // Fetch user, categories, subcategories, brands, vendors, and locations concurrently
        const [
          userRes, catRes, subCatRes, brandRes, vendorRes, locRes
        ] = await Promise.allSettled([
          fetchRelationalDoc("users", a.assignedTo, "uid"),
          fetchRelationalDoc("categories", a.categoryId, "categoryId"),
          fetchRelationalDoc("subcategories", a.subCategoryId, "subCategoryId"),
          fetchRelationalDoc("brands", a.brandId, "brandId"),
          fetchRelationalDoc("vendors", a.vendorId, "vendorId"),
          fetchRelationalDoc("locations", a.locationCode || a.locationId, "locationCode")
        ]);

        const resolvedUser = userRes.status === 'fulfilled' ? userRes.value : null;
        const resolvedCat = catRes.status === 'fulfilled' ? catRes.value : null;
        const resolvedSubCat = subCatRes.status === 'fulfilled' ? subCatRes.value : null;
        const resolvedBrand = brandRes.status === 'fulfilled' ? brandRes.value : null;
        const resolvedVendor = vendorRes.status === 'fulfilled' ? vendorRes.value : null;
        const resolvedLoc = locRes.status === 'fulfilled' ? locRes.value : null;

        if (resolvedUser) {
          setAssignedUserData({
            name: resolvedUser.displayName || resolvedUser.name || "Unknown User",
            department: resolvedUser.department || "No Department Logged",
          });
        }

        if (resolvedSubCat) {
          setSubCategory(resolvedSubCat);
        }

        let prefEnabled = false;
        if (a.companyId) {
          try {
            prefEnabled = await preferencesService.isPublicQrEnabled(a.companyId);
          } catch (prefErr) {
            prefEnabled = false;
          }
        }
        setPublicQrEnabled(Boolean(prefEnabled));

        // Save resolved display names with multi-level fallbacks
        setAsset({
          id: assetSnap.id,
          ...a,
          resolvedCategoryName: resolvedCat?.categoryName || resolvedCat?.name || a.categoryName || a.category,
          resolvedSubCategoryName: resolvedSubCat?.subCategoryName || resolvedSubCat?.name || a.subCategoryName || a.subCategory,
          resolvedBrandName: resolvedBrand?.brandName || resolvedBrand?.name || a.brandName || a.brand,
          resolvedVendorName: resolvedVendor?.vendorName || resolvedVendor?.name || a.vendorName || a.vendor,
          resolvedLocationName: resolvedLoc?.locationName || resolvedLoc?.name || a.currentLocationName || a.locationName || a.locationCode,
          resolvedLocationAddress: resolvedLoc?.address || resolvedLoc?.locationAddress || a.locationAddress,
          resolvedLocationCity: resolvedLoc?.city || a.locationCity,
          resolvedLocationState: resolvedLoc?.state || a.locationState,
          resolvedLocationZip: resolvedLoc?.zipCode || resolvedLoc?.postalCode,
        });

        setPrefResolved(true);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [assetId, token]);

  // Sync toggle state with loaded asset data
  useEffect(() => {
    if (asset) {
      setLocalIsTagged(asset.isTagged || false);
    }
  }, [asset?.isTagged]);

  // Decide view mode
  useEffect(() => {
    if (loading || error || !asset || !prefResolved) return;

    const loggedIn = Boolean(
      currentUser?.uid || currentUser?.id || currentUser?.email
    );

    if (loggedIn) {
      setViewMode("content");
    } else if (publicQrEnabled) {
      setViewMode("guest-choice");
    } else {
      setViewMode("login-required");
    }
  }, [loading, error, asset, prefResolved, currentUser, publicQrEnabled]);

  // Derived Flags
  const role = String(currentUser?.role || "").toLowerCase();
  const isAdmin = role === "admin";
  const isAuditor = role === "auditor";
  const isAdminOrAuditor = isAdmin || isAuditor;

  const taggingRequired = subCategory?.taggingRequired === true;
  const alreadyTagged = asset?.isTagged === true;

  const showTaggingPrompt =
    viewMode === "content" &&
    Boolean(currentUser) &&
    isAdminOrAuditor &&
    taggingRequired;

  const showGuestTaggingHint =
    viewMode === "content" &&
    !currentUser &&
    taggingRequired &&
    !alreadyTagged;

  const handleContinueAsGuest = () => setViewMode("content");

  const handleGoToLogin = () => {
    const redirect = encodeURIComponent(window.location.pathname + window.location.search);
    navigate(`/login?redirect=${redirect}`);
  };

  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 750 * 1024) {
      setTagError("Photo must be under 750 KB.");
      return;
    }
    if (!file.type.startsWith("image/")) {
      setTagError("Please select an image file.");
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      setTagPhoto(dataUrl);
      setTagError("");
    } catch (err) {
      setTagError("Failed to read image.");
    }
  };

  const saveTagStatus = async (nextTagged, photoDataUrl = null) => {
    setTagSubmitting(true);
    setTagError("");
    setTagSuccess(false);

    try {
      if (nextTagged) {
        if (isAuditor && !photoDataUrl && !asset?.tagPhotoUrl) {
          throw new Error("Please upload a physical tag photo to verify this asset.");
        }

        await assetService.markAssetTagged(
          asset.id,
          {
            taggedBy: currentUser?.email || currentUser?.displayName || "system",
            tagPhotoUrl: photoDataUrl || asset?.tagPhotoUrl || null,
            notes: "",
          },
          currentUser,
          { requirePhoto: isAuditor }
        );

        setAsset((prev) => ({
          ...prev,
          isTagged: true,
          taggedAt: new Date().toISOString(),
          taggedBy: currentUser?.email || "system",
          tagPhotoUrl: photoDataUrl || asset?.tagPhotoUrl || null,
        }));
        setLocalIsTagged(true);
        setTagPhoto(null);
      } else {
        if (typeof assetService.markAssetUntagged === "function") {
          await assetService.markAssetUntagged(asset.id, currentUser);
        } else {
          const assetRef = doc(db, "assets", asset.id);
          await updateDoc(assetRef, {
            isTagged: false,
            taggedAt: null,
            taggedBy: null,
            tagPhotoUrl: null,
          });
        }

        setAsset((prev) => ({
          ...prev,
          isTagged: false,
          taggedAt: null,
          taggedBy: null,
          tagPhotoUrl: null,
        }));
        setLocalIsTagged(false);
        setTagPhoto(null);
      }

      setTagSuccess(true);
      setTimeout(() => setTagSuccess(false), 2500);
    } catch (err) {
      setLocalIsTagged(asset?.isTagged === true);
      setTagError(err.message || "Failed to save tag verification.");
    } finally {
      setTagSubmitting(false);
    }
  };

  const handleToggleChange = async (checked) => {
    setTagError("");
    setTagSuccess(false);

    if (isAdmin) {
      setLocalIsTagged(checked);
      await saveTagStatus(checked, null);
      return;
    }

    if (!checked) {
      setLocalIsTagged(false);
      setTagPhoto(null);
      await saveTagStatus(false, null);
      return;
    }

    setLocalIsTagged(true);
  };

  const handleAuditorSubmitPhoto = () => {
    if (!tagPhoto && !asset?.tagPhotoUrl) {
      setTagError("Please upload a physical tag photo to verify this asset.");
      return;
    }
    saveTagStatus(true, tagPhoto || asset?.tagPhotoUrl || null);
  };

  const fmtDate = (ts) =>
    !ts
      ? null
      : (ts.toDate ? ts.toDate() : new Date(ts)).toLocaleDateString(undefined, {
          dateStyle: "medium",
        });

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER: LOADING & ERRORS
  // ─────────────────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', bgcolor: '#f1f5f9' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', bgcolor: '#f1f5f9', p: 3 }}>
        <Alert severity="error" sx={{ minWidth: 300 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Access Denied</Typography>
          <Typography variant="body2">{error}</Typography>
          <Button onClick={() => window.location.reload()} variant="outlined" color="error" size="small" sx={{ mt: 2 }}>
            Try Again
          </Button>
        </Alert>
      </Box>
    );
  }

  if (viewMode === "login-required") {
    return (
      <Box sx={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', bgcolor: '#f1f5f9', p: 3 }}>
        <SectionCard sx={{ maxWidth: 400, p: 3, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>Login Required</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            This organisation requires you to sign in before viewing asset details.
          </Typography>
          <Button variant="contained" fullWidth onClick={handleGoToLogin} sx={{ bgcolor: '#2563eb', textTransform: 'none' }}>
            Go to Login
          </Button>
        </SectionCard>
      </Box>
    );
  }

  if (viewMode === "guest-choice") {
    return (
      <Box sx={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', bgcolor: '#f1f5f9', p: 3 }}>
        <SectionCard sx={{ maxWidth: 400, p: 3, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>Asset Found</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            You can continue as a guest to view basic asset details, or log in for the full experience.
          </Typography>
          <Stack spacing={1.5}>
            <Button variant="outlined" fullWidth onClick={handleContinueAsGuest} sx={{ textTransform: 'none' }}>
              Continue as Guest
            </Button>
            <Button variant="contained" fullWidth onClick={handleGoToLogin} sx={{ bgcolor: '#2563eb', textTransform: 'none' }}>
              Login
            </Button>
          </Stack>
        </SectionCard>
      </Box>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER: MAIN CONTENT
  // ─────────────────────────────────────────────────────────────────────────────
  
  const supportHistoryData = asset.supportHistory || asset.maintenanceHistory || [];
  
  return (
    <Box sx={{ flex: 1, minHeight: '100vh', bgcolor: '#f1f5f9' }}>
      {/* APP BAR */}
      <AppBar position="sticky" elevation={0} sx={{ bgcolor: '#fff', borderBottom: '1px solid #e2e8f0' }}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <img 
              src="/logo.png" 
              alt="Logo" 
              style={{ 
                height: '36px', 
                width: 'auto', 
                objectFit: 'contain', 
                mixBlendMode: 'multiply'
              }} 
            />
            <Typography sx={{ color: '#1e293b', fontWeight: 700, fontSize: '1.125rem' }}>
              Asset Registry
            </Typography>
          </Box>
          {viewMode === "content" && !currentUser && publicQrEnabled && (
            <Chip label="Guest View" size="small" sx={{ fontWeight: 600, fontSize: '0.65rem', textTransform: 'uppercase' }} />
          )}
        </Toolbar>
      </AppBar>

      {/* SCROLLABLE BODY */}
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Stack spacing={3}>

          {/* Guest Tagging Hint */}
          {showGuestTaggingHint && (
            <Alert severity="warning" action={
              <Button color="inherit" size="small" onClick={handleGoToLogin} sx={{ fontWeight: 600 }}>Login to Verify</Button>
            }>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Verification Required</Typography>
              <Typography variant="body2">This asset's category requires a physical tag photo. Log in as an Admin or Auditor to verify.</Typography>
            </Alert>
          )}

          {/* ── TOGGLE TAGGING SECTION (Admin & Auditor) ── */}
          {showTaggingPrompt && (
            <SectionCard variant="outlined">
              <CardContent sx={{ p: 3 }}>
                <SectionTitle>Tag Verification Status</SectionTitle>
                
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ color: '#475569', mb: 0.5 }}>
                    {isAdmin ? "Toggle the tag verification status below." : "Tagged proof is required when verifying the tag."}
                  </Typography>
                  {asset.isTagged && asset.taggedAt && (
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                      Last verified on {fmtDate(asset.taggedAt)} by {asset.taggedBy || "system"}
                    </Typography>
                  )}
                </Box>

                <Box sx={{ 
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center', 
                  p: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 1,
                  maxWidth: 500
                }}>
                  <Box>
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#1e293b' }}>
                      {localIsTagged ? "Asset is verified as tagged" : "Asset is currently untagged"}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: '#6b7280', mt: 0.5 }}>
                      {tagSubmitting ? "Saving…" : localIsTagged ? "Switch off to clear verification status" : "Switch on to verify physical tag"}
                    </Typography>
                  </Box>
                  <Switch
                    checked={localIsTagged}
                    onChange={(e) => handleToggleChange(e.target.checked)}
                    disabled={tagSubmitting}
                    color="primary"
                  />
                </Box>

                {/* AUDITOR PHOTO REQUIREMENT */}
                {isAuditor && localIsTagged && (
                  <Box sx={{ mt: 3, maxWidth: 500 }}>
                    <FieldLabel>Tag Photo *</FieldLabel>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <input
                        id="tagPhotoUpload"
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={handlePhotoSelect}
                        disabled={tagSubmitting}
                        style={{ display: 'none' }}
                      />
                      <label htmlFor="tagPhotoUpload">
                        <Button
                          component="span"
                          variant="outlined"
                          size="small"
                          disabled={tagSubmitting}
                          startIcon={<AttachFileIcon />}
                          sx={{ textTransform: 'none', fontWeight: 500, borderStyle: 'dashed', borderColor: '#d1d5db', color: '#374151', '&:hover': { bgcolor: '#f3f4f6' } }}
                        >
                          {tagPhoto ? 'Change Photo' : 'Capture / Choose Photo'}
                        </Button>
                      </label>
                      
                      {(tagPhoto || asset?.tagPhotoUrl) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <img src={tagPhoto || asset.tagPhotoUrl} alt="Tag preview" style={{ width: 40, height: 40, borderRadius: 4, objectFit: 'cover', border: '1px solid #e2e8f0' }} />
                          <CheckCircleIcon sx={{ fontSize: 16, color: '#10b981' }} />
                        </Box>
                      )}
                    </Box>

                    {/* Submit Button for Auditor */}
                    {(tagPhoto || (!asset?.isTagged && localIsTagged)) && (
                      <Button
                        onClick={handleAuditorSubmitPhoto}
                        disabled={tagSubmitting || (!tagPhoto && !asset?.tagPhotoUrl)}
                        variant="contained"
                        size="small"
                        sx={{ bgcolor: '#2563eb', textTransform: 'none', fontWeight: 600 }}
                      >
                        {tagSubmitting ? <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} /> : null}
                        {tagSubmitting ? "Saving…" : "Upload & Verify"}
                      </Button>
                    )}
                  </Box>
                )}

                {/* Status Notifications */}
                {tagError && (
                  <Alert severity="error" sx={{ mt: 2, maxWidth: 500, py: 0 }}>{tagError}</Alert>
                )}
                {tagSuccess && (
                  <Alert severity="success" sx={{ mt: 2, maxWidth: 500, py: 0 }}>Verification status saved successfully!</Alert>
                )}
              </CardContent>
            </SectionCard>
          )}

          {/* ── CORE IDENTITY SECTION ── */}
          <SectionCard variant="outlined">
            <CardContent sx={{ p: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'center', sm: 'flex-start' }, gap: 3 }}>
              
              {/* Image or Fallback Logo */}
              {asset.imageUrl ? (
                <img src={asset.imageUrl} alt={asset.assetName} style={{ width: 100, height: 100, borderRadius: 8, objectFit: 'cover', border: '1px solid #e2e8f0' }} />
              ) : (
                <Box sx={{ width: 120, height: 90, display: 'flex', alignItems: 'center', justifyContent: { xs: 'center', sm: 'flex-start' } }}>
                  <a href="https://niyantrix.in" target="_blank" rel="noopener noreferrer" style={{ display: 'flex' }}>
                    <img 
                      src="/logo.png" 
                      alt="Niyantrix Logo" 
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '56px', 
                        objectFit: 'contain',
                        mixBlendMode: 'multiply'
                      }} 
                    />
                  </a>
                </Box>
              )}
              
              <Box sx={{ flex: 1, textAlign: { xs: 'center', sm: 'left' } }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#1e293b', mb: 0.5 }}>
                  {asset.assetName}
                </Typography>
                <Typography sx={{ fontSize: '0.875rem', color: '#64748b', fontWeight: 500, mb: 2 }}>
                  Asset ID: <span style={{ color: '#0f172a', fontWeight: 600 }}>{asset.assetId || "N/A"}</span>
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'center', sm: 'flex-start' }, gap: 2 }}>
                  {asset.status?.toLowerCase() === "assigned" ? (
                    <Box sx={{ p: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 1, display: 'inline-block', textAlign: 'left' }}>
                      <FieldLabel sx={{ mb: 1 }}>Assigned To</FieldLabel>
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#1e293b' }}>
                        {assignedUserData?.name || asset.assignedToName || "Unassigned"}
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>
                        {assignedUserData?.department || asset.assignedToDepartment || "No Department Logged"}
                      </Typography>
                    </Box>
                  ) : (
                    <Chip label={asset.status || "Active"} size="small" sx={{ bgcolor: '#ecfdf5', color: '#065f46', fontWeight: 600, border: '1px solid #a7f3d0' }} />
                  )}
                </Box>
              </Box>
            </CardContent>
          </SectionCard>

          {/* ── ASSET SPECIFICATIONS ── */}
          <SectionCard variant="outlined">
            <CardContent sx={{ p: 3 }}>
              <SectionTitle>Asset Specifications</SectionTitle>
              <FormRow>
                <DetailValue label="Manufacturer / Brand" value={asset.resolvedBrandName} />
                <DetailValue label="Serial Number" value={asset.manufacturerSerialNumber || asset.serialNumber} isMonospace />
                <DetailValue label="Current Condition" value={asset.condition || asset.status} />
              </FormRow>
              <FormRow>
                <DetailValue label="Asset Category" value={asset.resolvedCategoryName} />
                <DetailValue label="Sub-Category Line" value={asset.resolvedSubCategoryName} />
              </FormRow>
            </CardContent>
          </SectionCard>

          {/* ── PURCHASE & WARRANTY LEDGER ── */}
          <SectionCard variant="outlined">
            <CardContent sx={{ p: 3 }}>
              <SectionTitle>Purchase & Warranty Ledger</SectionTitle>
              <FormRow>
                <DetailValue label="Authorized Vendor" value={asset.resolvedVendorName} />
                <DetailValue label="Invoice Reference" value={asset.invoiceNumber || asset.invoiceNo || asset.purchaseOrderNumber} isMonospace />
              </FormRow>
              <FormRow>
                <DetailValue label="Date of Procurement" value={fmtDate(asset.purchaseDate || asset.dateOfPurchase)} />
                <DetailValue label="Warranty Coverage Until" value={fmtDate(asset.warrantyExpiry || asset.warrantyExpiryDate || asset.warrantyEndDate)} />
              </FormRow>
            </CardContent>
          </SectionCard>

          {/* ── CURRENT LOCATION HUB ── */}
          <SectionCard variant="outlined">
            <CardContent sx={{ p: 3 }}>
              <SectionTitle>Current Location Hub</SectionTitle>
              <FormRow>
                <DetailValue label="Assigned Facility" value={asset.resolvedLocationName || asset.locationCode || "Unassigned Depot"} />
                <DetailGroup>
                  <FieldLabel>Registered Address Data</FieldLabel>
                  {asset.resolvedLocationAddress ? (
                    <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#1e293b' }}>
                      {asset.resolvedLocationAddress}
                      {asset.resolvedLocationCity ? `, ${asset.resolvedLocationCity}` : ''}
                      <br />
                      <span style={{ color: '#64748b', fontSize: '0.75rem' }}>
                        {[asset.resolvedLocationState, asset.resolvedLocationZip].filter(Boolean).join(' • ')}
                      </span>
                    </Typography>
                  ) : (
                    <Typography sx={{ fontSize: '0.875rem', fontStyle: 'italic', color: '#9ca3af' }}>No geographic breakdown logged.</Typography>
                  )}
                </DetailGroup>
              </FormRow>
            </CardContent>
          </SectionCard>

          {/* ── SUPPORT HISTORY ── */}
          <SectionCard variant="outlined">
            <CardContent sx={{ p: 3 }}>
              <SectionTitle>Support History</SectionTitle>
              {supportHistoryData.length > 0 ? (
                <Stack spacing={3}>
                  {supportHistoryData.map((item, index) => (
                    <Box key={index} sx={{ pl: 2, borderLeft: '2px solid #e2e8f0' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#1e293b' }}>
                          {item.title || item.action || "Maintenance Activity"}
                        </Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>
                          {fmtDate(item.date)}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: '0.8125rem', color: '#475569' }}>
                        {item.notes || item.reason || "No activity log descriptions attached."}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <Typography sx={{ fontSize: '0.875rem', fontStyle: 'italic', color: '#9ca3af', textAlign: 'center', py: 2 }}>
                  Support history not logged
                </Typography>
              )}
            </CardContent>
          </SectionCard>

        </Stack>
      </Container>
    </Box>
  );
};

export default PublicAssetView;